import { HomeOutlined, ReloadOutlined } from '@ant-design/icons'
import { Alert, Breadcrumb, Button, Empty, Segmented, Skeleton, Space } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  fetchSiteDraftPreview,
  fetchSitePublishedPreview,
  siteDraftPreviewBaseUrl,
  sitePublishedPreviewBaseUrl,
} from '../../api'
import type { SaasApiError } from '../../api'

type Mode = 'draft' | 'published'

/** Injects a tiny click-interceptor before `</body>` so internal links
 * rendered by the theme (e.g. `<a href="/collections/chinese-art">`,
 * see `collection_website.py`'s templates) navigate this Preview panel
 * to that path instead of trying to load it against the browser's own
 * top-level origin — this is what makes the rendered website *actually
 * clickable* inside Vistoera (spec §11: "真实、可点击") rather than a
 * static screenshot-like dump.
 *
 * Also rewrites the theme's own root-absolute stylesheet hrefs (e.g.
 * `/static/classic-collection.css`, see `collection_website.py`'s
 * `_SHARED_HEAD`) into this same Preview endpoint with a `path` query
 * param — a `srcDoc` iframe has no URL of its own, so a root-absolute
 * href otherwise resolves against the *parent* page's origin (missing
 * the endpoint entirely) instead of back through this API call, which
 * silently drops the theme's real CSS and leaves Preview looking
 * unstyled even though the HTML/content is correct. */
function withNavigationBridge(html: string, previewBaseUrl: string): string {
  const withRewrittenAssets = html.replace(
    /href="(\/static\/[^"]+)"/g,
    (_match, href: string) => `href="${previewBaseUrl}?path=${encodeURIComponent(href)}"`,
  )
  const script = `<script>document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a') : null;
    if (!a) return;
    var href = a.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('/static/')) return;
    e.preventDefault();
    window.parent.postMessage({ source: 'vistoera-website-preview', path: href }, '*');
  });</script>`
  if (withRewrittenAssets.includes('</body>')) return withRewrittenAssets.replace('</body>', `${script}</body>`)
  return withRewrittenAssets + script
}

/**
 * Preview tab — spec §11/§12's core exit gate: a real rendered website
 * page (Header / Collection Name / Description / Item cards / Images),
 * never JSON, never an empty iframe shell. "Draft Preview" always
 * re-renders current DB state; "Published Snapshot" reads back exactly
 * what the last `/publish` call wrote (spec §14) — switching modes here
 * is the visible proof those two are actually different code paths.
 */
export default function PreviewTab({ workspaceId, siteId }: { workspaceId: string; siteId: string }) {
  const [mode, setMode] = useState<Mode>('draft')
  const [path, setPath] = useState('/')
  const [html, setHtml] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    setNotFound(false)
    const fetcher = mode === 'draft' ? fetchSiteDraftPreview : fetchSitePublishedPreview
    fetcher(workspaceId, siteId, path)
      .then((r) => setHtml(r.data))
      .catch((err) => {
        const apiErr = err as SaasApiError
        if (apiErr.status === 404) {
          setNotFound(true)
          setHtml(null)
        } else {
          setError(apiErr.message)
        }
      })
      .finally(() => setLoading(false))
  }, [workspaceId, siteId, mode, path])

  useEffect(load, [load])

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.source === 'vistoera-website-preview' && typeof event.data.path === 'string') {
        setPath(event.data.path)
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const crumbs = path
    .split('/')
    .filter(Boolean)
    .map((segment, idx, arr) => ({ title: segment, path: '/' + arr.slice(0, idx + 1).join('/') }))

  return (
    <div style={{ marginTop: 16 }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Segmented
          value={mode}
          onChange={(v) => {
            setMode(v as Mode)
            setPath('/')
          }}
          options={[
            { label: '草稿预览', value: 'draft' },
            { label: '已发布快照', value: 'published' },
          ]}
        />
        <Button icon={<HomeOutlined />} onClick={() => setPath('/')} disabled={path === '/'}>
          首页
        </Button>
        <Button icon={<ReloadOutlined />} onClick={load}>
          刷新
        </Button>
        <Breadcrumb
          items={[
            { title: <HomeOutlined />, onClick: () => setPath('/') },
            ...crumbs.map((c) => ({ title: c.title, onClick: () => setPath(c.path) })),
          ]}
        />
      </Space>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      <div
        style={{
          border: '1px solid #e0e0e0',
          borderRadius: 6,
          minHeight: 480,
          background: '#fff',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} style={{ padding: 24 }} />
        ) : notFound ? (
          <Empty
            style={{ marginTop: 100, marginBottom: 100 }}
            description={
              mode === 'draft'
                ? '暂无可预览内容——请先创建条目或其他内容。'
                : '尚未发布——请前往"发布"完成首次发布。'
            }
          />
        ) : html ? (
          <iframe
            title="网站预览"
            srcDoc={withNavigationBridge(
              html,
              mode === 'draft' ? siteDraftPreviewBaseUrl(workspaceId, siteId) : sitePublishedPreviewBaseUrl(workspaceId, siteId),
            )}
                style={{ width: '100%', height: 640, border: 'none', display: 'block' }}
                // `allow-same-origin` is required alongside `allow-scripts`
                // here: without it, a `srcDoc` iframe gets an *opaque*
                // origin, which makes Chrome's Opaque Response Blocking
                // (ORB) reject even same-host `<link rel="stylesheet">`
                // fetches (net::ERR_BLOCKED_BY_ORB) — silently dropping the
                // theme's real CSS. The rendered content is our own
                // trusted backend template output (Jinja-escaped entry
                // text, never third-party/attacker HTML), so this is not
                // exposing the parent app to untrusted script.
                sandbox="allow-scripts allow-same-origin"
              />
        ) : null}
      </div>
    </div>
  )
}
